/* eslint-disable no-nested-ternary */
/**
 * ConstructorTransformer.js
 * Transforms Constructor.io Search API response → JCP Solr response shape.
 * Used by GallerySaga for search pages when Constructor is enabled.
 *
 * All functions are null-safe — if Constructor returns unexpected data,
 * the transformer returns valid empty structures so reducers don't crash.
 */

import {
    CONSTRUCTOR_IGNORE_FILTER_PARAMS,
    CONSTRUCTOR_FACET_CONFIG,
    PREVIEWABLE_FIELDS_KEY,
} from '../common/constants';
import { isStoreFacet } from './ConstructorBOPISHelper';

function hashCode(str) {
    if (!str || typeof str !== 'string') return 0;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0;
    }
    return Math.abs(hash);
}

function slugify(str) {
    if (!str || typeof str !== 'string') return '';
    return str
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}

function buildCanonicalURL(term, facets, pageType, categoryId, galleryPath, brandParam) {
    let url;
    if (pageType === 'g') {
        const path = galleryPath || 'g';
        if (categoryId) {
            url = `/${path}?id=${encodeURIComponent(categoryId)}`;
        } else if (brandParam) {
            // Brand page without category ID - preserve brand param
            url = `/${path}?brand=${encodeURIComponent(brandParam)}`;
        } else {
            url = `/${path}`;
        }
    } else {
        url = `/s?searchTerm=${encodeURIComponent(term || '')}`;
    }
    if (!Array.isArray(facets)) return url;
    facets.forEach((facet) => {
        if (!facet || !Array.isArray(facet.options)) return;
        facet.options.forEach((opt) => {
            if (opt && opt.status === 'selected' && opt.value) {
                url += `&${encodeURIComponent(facet.name || '')}=${encodeURIComponent(opt.value)}`;
            }
        });
    });
    return url;
}

/**
 * Returns just the path portion (no query string) for gallery/search API URLs.
 * Used to build URLs where facets must be inserted before id=/searchTerm=.
 */
function getBaseApiPathOnly(term, pageType, categoryId, galleryPath, brandParam) {
    if (pageType === 'g') {
        const path = galleryPath || 'g';
        if (categoryId || brandParam) {
            return `/v1/search-service/${path}`;
        }
    }
    return '/v1/search-service/s';
}

/**
 * Returns the anchor query param(s) that must come after facets in the URL.
 * For gallery: ?id=cat... or ?brand=...
 * For search: ?searchTerm=...
 */
function getBaseApiAnchorParam(term, pageType, categoryId, brandParam) {
    if (pageType === 'g') {
        if (categoryId) return `id=${encodeURIComponent(categoryId)}`;
        if (brandParam) return `brand=${encodeURIComponent(brandParam)}`;
    }
    const encodedTerm = encodeURIComponent(term || '').replace(/%20/g, '+');
    return `searchTerm=${encodedTerm}`;
}

/**
 * Preview: Returns true if the given facet name is a preview-hidden facet whose values
 * contain a |dayKey suffix that should be stripped from display/URL.
 */
function isPreviewHiddenFacet(facetName, previewDayKey, previewDateFacetOptions) {
    return (
        !!previewDayKey &&
        Array.isArray(previewDateFacetOptions) &&
        previewDateFacetOptions.includes(facetName)
    );
}

/**
 * Strips the |dayKey suffix from a preview facet value for display/URL use.
 * e.g. "SALE|19Sep2026" → "SALE"
 */
function stripPreviewSuffix(value) {
    if (!value || !value.includes('|')) return value;
    return value.split('|')[0];
}

/**
 * Builds a query string of passthrough params that should be preserved in facet/sort URLs.
 * These params are not filters but should remain in the URL during navigation.
 *
 * Passthrough params are determined dynamically: any URL param that is NOT in
 * CONSTRUCTOR_IGNORE_FILTER_PARAMS and is NOT a recognized facet name will be
 * automatically passed through. This allows unknown/custom params to be preserved
 * without requiring explicit configuration.
 *
 * @param {object} params - URL params object
 * @param {Array} facetNames - Array of facet names from the current response (optional)
 * @returns {object} - Map of passthrough params { key: encodedValue } for inclusion in facetsMap
 */
function getPassthroughParamsMap(params, facetNames = []) {
    if (!params || typeof params !== 'object') return {};

    // Build a Set of ignored params (lowercase for case-insensitive matching)
    const ignoreSet = new Set(
        CONSTRUCTOR_IGNORE_FILTER_PARAMS.map((p) => (p ? p.toLowerCase() : p))
    );

    // Build a Set of known facet names (lowercase for case-insensitive matching)
    const facetSet = new Set(facetNames.map((f) => (f ? f.toLowerCase() : f)));

    // System params that are handled separately and should not be passed through
    const systemParams = new Set(['productgridview', 'id', 'searchterm', 'brand']);

    const result = {};
    Object.keys(params).forEach((key) => {
        const keyLower = key.toLowerCase();
        // Pass through if: not ignored, not a facet, not a system param, and has a value
        if (
            !ignoreSet.has(keyLower) &&
            !facetSet.has(keyLower) &&
            !systemParams.has(keyLower) &&
            params[key]
        ) {
            result[key] = encodeURIComponent(params[key]).replace(/%20/g, '+');
        }
    });
    return result;
}

/**
 * Builds a full API URL with facets in alphabetical order before id=/searchTerm=.
 * facetsMap: { facetName: 'encodedValue', ... } — only include facets that should appear.
 * trailingParams: string of additional params to append after productGridView (e.g. &sort=BS)
 */
function buildFacetUrl(pathOnly, facetsMap, anchorParam, trailingParams) {
    const anchorKey = anchorParam ? anchorParam.slice(0, anchorParam.indexOf('=')) : '';
    const sortedFacetParams = Object.keys(facetsMap)
        .sort()
        .filter((k) => facetsMap[k])
        .map((k) => `${k}=${facetsMap[k]}`)
        .join('&');
    if (anchorKey === 'brand') {
        // Brand pages: all params alphabetical including brand=
        const allParams = { ...facetsMap, [anchorKey]: anchorParam.slice(anchorKey.length + 1) };
        const sorted = Object.keys(allParams)
            .sort()
            .filter((k) => allParams[k])
            .map((k) => `${k}=${allParams[k]}`)
            .join('&');
        return `${pathOnly}?${sorted}&productGridView=medium${trailingParams || ''}`;
    }
    if (anchorKey === 'searchTerm') {
        // Search pages: searchTerm= first, then facets alphabetically
        const facetPart = sortedFacetParams ? `&${sortedFacetParams}` : '';
        return `${pathOnly}?${anchorParam}${facetPart}&productGridView=medium${
            trailingParams || ''
        }`;
    }
    // Gallery pages: facets alphabetically, then id= after
    const facetPart = sortedFacetParams ? `${sortedFacetParams}&` : '';
    return `${pathOnly}?${facetPart}${anchorParam}&productGridView=medium${trailingParams || ''}`;
}

/**
 * Builds the base API path for facet/sort/chip URLs based on page type.
 * Search: /v1/search-service/s?searchTerm={term}
 * Gallery/Browse: /v1/search-service/{pathname}?id={categoryId}
 * Brand pages: /v1/search-service/{pathname}?brand={brandValue}
 * The gallery pathname preserves the original URL path (e.g., /g/men/mens-socks or /g/brand)
 */
function getBaseApiPath(term, pageType, categoryId, galleryPath, brandParam) {
    if (pageType === 'g') {
        const path = galleryPath || 'g';
        if (categoryId) {
            return `/v1/search-service/${path}?id=${encodeURIComponent(categoryId)}`;
        }
        if (brandParam) {
            // Brand page without category ID - preserve brand param
            return `/v1/search-service/${path}?brand=${encodeURIComponent(brandParam)}`;
        }
        // Fallback to search if no categoryId or brandParam
    }
    const encodedTerm = encodeURIComponent(term || '').replace(/%20/g, '+');
    return `/v1/search-service/s?searchTerm=${encodedTerm}`;
}

function encodeFacetValue(value) {
    return encodeURIComponent(value).replace(/%20/g, '+').replace(/!/g, '%21');
}

function roundPrice(value) {
    if (typeof value !== 'number' || isNaN(value)) return 0;
    return Math.round(value * 100) / 100;
}

function transformProduct(result, searchTerm, config) {
    if (!result || typeof result !== 'object') {
        return { name: '', ppId: '', skuSwatch: [], availabilityStatus: [], imagesInfo: {} };
    }
    const d = result.data || {};
    // preview: price fields are arrays of "value|dayKey" strings,
    const previewDayKey = config && config.previewDayKey;
    const pf =
        previewDayKey && d[PREVIEWABLE_FIELDS_KEY] && d[PREVIEWABLE_FIELDS_KEY][previewDayKey];
    const rawOffers = Array.isArray(d.offers) ? d.offers : [];
    const offers = rawOffers.filter((o) => o && o.inventoryStatus === 'Available');
    // Also check for winningOffer (Mirakl marketplace products have this as single object in data)
    const winningOffer = (pf && pf.winningOffer) || d.winningOffer || null;
    const hasMarketplaceOffers =
        offers.some((o) => o && o.shopName && o.shopName !== '') ||
        (winningOffer && winningOffer.shop_name);
    const sourceType = hasMarketplaceOffers ? 'MKL' : d.source === 'MKL' ? 'MKL' : null;

    let currentMin = 0;
    let currentMax = 0;
    if (winningOffer && winningOffer.Offer_Min_Price) {
        // Mirakl winning offer takes priority
        currentMin = roundPrice(winningOffer.Offer_Min_Price);
        currentMax = roundPrice(winningOffer.Offer_Max_Price || winningOffer.Offer_Min_Price);
    } else if (offers.length > 0) {
        const minPrices = offers.map((o) =>
            typeof o.offerMinPrice === 'number' ? o.offerMinPrice : 0
        );
        const maxPrices = offers.map((o) =>
            typeof o.offerMaxPrice === 'number' ? o.offerMaxPrice : 0
        );
        currentMin = roundPrice(Math.min(...minPrices));
        currentMax = roundPrice(Math.max(...maxPrices));
    } else {
        currentMin = roundPrice((pf && pf.price_min) ?? d.price_min ?? d.price ?? 0);
        currentMax = roundPrice((pf && pf.price_max) ?? d.price_max ?? d.price ?? currentMin);
    }
    const originalMin = roundPrice(
        (pf && pf.original_price_min) ?? d.original_price_min ?? currentMin
    );
    const originalMax = roundPrice(
        (pf && pf.original_price_max) ?? d.original_price_max ?? currentMax
    );

    // Preview: honoring the price type attribute in pf over clearance flag
    let priceType = 'ORIGINAL';
    if ((pf && pf.clearance === 'Y') || (pf && pf.price_type === 'CLEARANCE')) {
        priceType = 'CLEARANCE';
    } else if (!pf && d.clearance === 'Y') {
        priceType = 'CLEARANCE';
    } else if (currentMin < originalMin) {
        priceType = 'SALE';
    }

    let marketingLabel = (pf && pf.marketing_label) || d.marketing_label || '';
    if (winningOffer && winningOffer.marketing_label) {
        marketingLabel = winningOffer.marketing_label;
    }

    let bulletedCopyAttrs = [];
    if (d.description && typeof d.description === 'string') {
        try {
            const m = d.description.match(/<li>(.*?)<\/li>/g);
            if (m) bulletedCopyAttrs = m.map((li) => li.replace(/<\/?li>/g, ''));
        } catch (e) {
            bulletedCopyAttrs = [];
        }
    }

    const badges = Array.isArray(d.badges) ? d.badges : [];
    const upperLeftBadge = [];
    const bottomBadges = [];
    if (d.bundle_indicator) {
        upperLeftBadge.push({
            name: `Save up to $${roundPrice(Math.max(0, originalMin - currentMin)).toFixed(0)}`,
            priority: 1,
            urlParam: 'bundle',
        });
    }
    if (badges.includes('NEW_BADGE') || badges.includes('NEW') || d.arrival === 'NEW') {
        upperLeftBadge.push({ name: 'NEW', priority: 2, urlParam: 'new' });
    }
    if (badges.includes('FEW_LEFT')) {
        upperLeftBadge.push({ name: 'Few Left', priority: 3, urlParam: 'fewleft' });
    }
    if (badges.includes('ONLY_AT_JCP')) {
        upperLeftBadge.push({ name: 'Only at JCP', priority: 4, urlParam: 'onlyatjcp' });
    }
    badges.forEach((b, i) => {
        if (
            typeof b === 'string' &&
            (b.includes('HUSKY') || b.includes('PLUS') || b.includes('PETITE'))
        ) {
            bottomBadges.push({
                name: b.replace(/_/g, ' ').replace('APPAREL ', ''),
                priority: i + 1,
                urlParam: slugify(b),
            });
        }
    });

    const variationsMap = Array.isArray(result.variations_map) ? result.variations_map : [];
    const skuSwatch = variationsMap
        .map((swatch, i) => {
            if (!swatch || typeof swatch !== 'object') return null;
            if (sourceType !== 'MKL' && !swatch.swatch_image) {
                return null;
            }
            const altImgs = Array.isArray(swatch.alt_images) ? swatch.alt_images : [];
            return {
                colorName: swatch.colors || swatch.color || '',
                swatchImageId: swatch.swatch_image || '',
                colorizedImageId: swatch.image_url || '',
                colorSeqNum: i + 1,
                skuId: swatch.variation_id || d.variation_id || '',
                defaultSwatch: swatch.is_default || false,
                skuAltImages: altImgs.map((img, j) => ({
                    imgId: typeof img === 'string' ? img : '',
                    colorSeq: j,
                })),
            };
        })
        .filter(Boolean);

    let pdpUrl = '';
    if (d.url && typeof d.url === 'string') {
        pdpUrl = d.url;
        // Only append searchTerm for search pages (pageType='s'), not gallery/browse pages (pageType='g')
        // Gallery pages use categoryId, not searchTerm, so appending it creates invalid URLs
        const isSearchPage = config && config.pageType === 's';
        if (isSearchPage && searchTerm) {
            const urlParamSeparator = pdpUrl.includes('?') ? '&' : '?';
            pdpUrl = `${pdpUrl}${urlParamSeparator}searchTerm=${encodeURIComponent(
                searchTerm
            ).replace(/%20/g, '+')}`;
        }
        // Append facetSelected param when PDP-enabled facets are active (matches Solr behavior)
        if (config && config.selectedFacets && config.selectedFacets.length > 0) {
            const urlParamSeparator = pdpUrl.includes('?') ? '&' : '?';
            pdpUrl += `${urlParamSeparator}facetSelected=${config.selectedFacets.join(',')}`;
        }
        // Append selectedSKUId when color facet is active — use the product's default variation_id
        // Solr always appends the skuId (default variation) when facetSelected is active,
        // PDP uses facetSelected + selectedSKUId together to determine which swatch to display
        if (config && config.selectedFacets && config.selectedFacets.length > 0) {
            const skuId = d.variation_id || '';
            if (skuId) {
                const urlParamSeparator = pdpUrl.includes('?') ? '&' : '?';
                pdpUrl += `${urlParamSeparator}selectedSKUId=${skuId}`;
            }
        }
        // Append storeIds to PDP URL when same-day-pickup is active (matches Solr behavior)
        if (config && config.params && config.params.storeIds) {
            const urlParamSeparator = pdpUrl.includes('?') ? '&' : '?';
            pdpUrl += `${urlParamSeparator}storeIds=${encodeURIComponent(config.params.storeIds)}`;
        }
        // Append geoZip to PDP URL when localization is active (matches Solr behavior)
        if (config && config.params && config.params.geoZip) {
            const urlParamSeparator = pdpUrl.includes('?') ? '&' : '?';
            pdpUrl += `${urlParamSeparator}geoZip=${encodeURIComponent(config.params.geoZip)}`;
        }
    }

    return {
        name: d.product_title || result.value || '',
        ppId: d.id || '',
        averageRating: d.rating || 0,
        reviewCount: d.review_count || 0,
        currentMin,
        currentMax,
        originalMin,
        originalMax,
        priceType,
        currentPriceLabel: priceType === 'ORIGINAL' ? '' : priceType,
        originalPriceLabel: priceType === 'SALE' || priceType === 'CLEARANCE' ? 'original' : '',
        minSavePrice:
            d.coupon_price_min || d.coupon_price_max
                ? 0
                : originalMin > 0 && currentMin < originalMin
                ? Math.round(((originalMin - currentMin) / originalMin) * 100)
                : 0,
        maxSavePrice:
            d.coupon_price_min || d.coupon_price_max
                ? 0
                : originalMax > 0 && currentMax < originalMax
                ? Math.round(((originalMax - currentMax) / originalMax) * 100)
                : 0,
        brand: d.brand || '',
        skuId: d.variation_id || '',
        pdpUrl,
        boostedPP: result.is_slotted || false,
        preOrder: d.preorder || false,
        videoId: d.video_id || '',
        customerFavourite: d.customer_favorite || false,
        marketingLabel,
        upperLeftBadge,
        bottomBadges,
        mapPrice:
            typeof d.marketing_promotions === 'string' &&
            d.marketing_promotions.includes('VIEW PRICE IN BAG'),
        imagesInfo: {
            // Use group_image only for specific /g categories (configurable via preferences.constructorIO.search.groupImageCategories)
            thumbnailImageId: (() => {
                const groupImageCategories = (config && config.groupImageCategories) || [];
                const isGalleryPage = config && config.pageType === 'g';
                const catId = config && config.categoryId;
                const useGroupImage =
                    isGalleryPage && catId && groupImageCategories.includes(catId) && d.group_image;
                return useGroupImage ? d.group_image : d.image_url || '';
            })(),
            altThumbnailImageId: (Array.isArray(d.alt_images) && d.alt_images[0]) || '',
        },
        currencyCode: 'USD',
        skuSwatch,
        sephoraIn: false,
        showComparison: priceType !== 'ORIGINAL' || !!d.bundle_indicator,
        truckItemIn: d.truckable || false,
        ecoRebate: d.rebate_eligible || false,
        bundleStatusBadge: d.bundle_indicator || false,
        bundleIndicator: !!(d.bundle_indicator && d.template_type === 'package'),
        appendSKUId: false,
        enableMiniPDP: (() => {
            // Determine template type: use data field first, fallback to pTmplType in URL
            const templateType =
                d.template_type ||
                (d.url && d.url.match(/pTmplType=([^&]+)/)
                    ? d.url.match(/pTmplType=([^&]+)/)[1]
                    : '');
            const allowedTypes = (config && config.templateTypesForMiniPdp) || [
                'regular',
                'sephora',
                'beauty',
            ];
            return !templateType || allowedTypes.includes(templateType);
        })(),
        subDivision: d.subdivision_id || '',
        firstActivationDate: d.first_active_date || '',
        bulletedCopyAttrs,
        fpacPriceMin: (pf && pf.coupon_price_min) ?? d.coupon_price_min ?? null,
        fpacPriceMax: (pf && pf.coupon_price_max) ?? d.coupon_price_max ?? null,
        fpacCoupon: (pf && pf.coupon_code) || d.coupon_code || null,
        channelMessage: d.online_only === 'Y' ? 'Online Only' : '',
        promotionMessages: (() => {
            const promo = Array.isArray(d.marketing_promotions)
                ? d.marketing_promotions[0]
                : d.marketing_promotions;
            return promo ? [{ message: promo, nfor: false }] : [];
        })(),
        availabilityStatus: [],
        newArrival: d.arrival === 'NEW' || badges.includes('NEW_BADGE') || badges.includes('NEW'),
        sourceType,
        lotId: d.lot_id || '',
        bopisEligibility: d.bopis_eligibility === 'Y',
        offers: winningOffer
            ? [
                  {
                      offerId: winningOffer.Offer_Id || '',
                      shopId: winningOffer.shop_id || '',
                      shopName: winningOffer.shop_name || '',
                      skuId: winningOffer.variation_id || '',
                      offerMinPrice: winningOffer.Offer_Min_Price || 0,
                      offerMaxPrice: winningOffer.Offer_Max_Price || 0,
                      inventoryStatus:
                          winningOffer.Inventory_status === 1 ? 'Available' : 'Unavailable',
                  },
              ]
            : offers.length > 0
            ? offers
            : undefined,
    };
}

function transformFacet(
    facet,
    term,
    currentSort,
    allFacets,
    params,
    pageType,
    categoryId,
    galleryPath,
    brandParam,
    previewDateFacetOptions,
    previewDayKey,
    appliedFilters,
    useBrowserPath
) {
    if (!facet || typeof facet !== 'object') return null;
    const options = Array.isArray(facet.options) ? facet.options : [];
    const typeMap = { multiple: 'checkbox', single: 'checkbox', range: 'prange' };
    const facetName = facet.name || '';
    const urlKey = facetName;
    const pathOnly = useBrowserPath
        ? galleryPath
            ? `/${galleryPath}`
            : pageType === 'g'
            ? '/g'
            : '/s'
        : getBaseApiPathOnly(term, pageType, categoryId, galleryPath, brandParam);
    const anchorParam = getBaseApiAnchorParam(term, pageType, categoryId, brandParam);
    const sortParam = currentSort ? `&sort=${currentSort}` : '';
    // Get passthrough params to include in facetsMap for alphabetical sorting
    // Extract facet names from allFacets for the passthrough logic
    const facetNames = Array.isArray(allFacets)
        ? allFacets.map((f) => f && f.name).filter(Boolean)
        : [];
    const passthroughParamsMap = getPassthroughParamsMap(params, facetNames);
    const trailingParams =
        sortParam +
        (params && params.Nf ? `&Nf=${encodeURIComponent(params.Nf).replace(/%20/g, '+')}` : '') +
        (params && params.Dr ? `&Dr=${encodeURIComponent(params.Dr).replace(/%20/g, '+')}` : '') +
        (params && params.cm_re
            ? `&cm_re=${encodeURIComponent(params.cm_re).replace(/%20/g, '+')}`
            : '');

    // Build a map of other currently-selected facets (excluding this facet and store facets)
    const otherFacetsMap = {};
    if (Array.isArray(allFacets)) {
        allFacets.forEach((otherFacet) => {
            if (!otherFacet || otherFacet.name === facetName || !Array.isArray(otherFacet.options))
                return;
            if (otherFacet.name === 'store_availability' || otherFacet.name === 'store_ids') return;
            const otherSelected = otherFacet.options
                .filter((o) => o && o.status === 'selected' && o.value)
                .map((o) => o.value);
            if (otherSelected.length > 0) {
                const displayValues = isPreviewHiddenFacet(
                    otherFacet.name,
                    previewDayKey,
                    previewDateFacetOptions
                )
                    ? otherSelected.map(stripPreviewSuffix)
                    : otherSelected;
                otherFacetsMap[otherFacet.name] = encodeFacetValue(displayValues.sort().join('|'));
            }
        });
    }
    // In preview, persist item_status in facet URLs
    if (
        previewDayKey &&
        appliedFilters &&
        appliedFilters['filters[item_status]'] &&
        facetName !== 'item_status'
    ) {
        const values = appliedFilters['filters[item_status]'];
        if (Array.isArray(values) && values.length > 0) {
            otherFacetsMap.item_status = encodeFacetValue(values.sort().join('|'));
        }
    }
    // Add passthrough params (like view_all) to otherFacetsMap so they sort alphabetically
    Object.assign(otherFacetsMap, passthroughParamsMap);
    /* When store_availability is active, it must sort alphabetically with other facets
     * (before id=) so it goes into facetsMap, not trailing params.
     * storeIds is not a filter — it stays as a trailing param. */
    const storeActive = !!(params && params.storeIds);
    const storeTrailingSuffix = storeActive ? `&storeIds=${params.storeIds}` : '';

    const selectedValues = options
        .filter((o) => o && o.status === 'selected')
        .map((o) => o.value || '')
        .filter(Boolean);

    return {
        facetName: facet.display_name || facet.name || '',
        facetId: hashCode(facetName),
        facetType:
            CONSTRUCTOR_FACET_CONFIG.facetTypes[facetName] ||
            typeMap[facet.type] ||
            CONSTRUCTOR_FACET_CONFIG.defaultFacetType,
        facetInternalName: facetName,
        facetValueCount: options.length,
        noOfSelectedFacetValues: selectedValues.length,
        searchAvailable:
            CONSTRUCTOR_FACET_CONFIG.searchableFiltersMap[facetName] !== undefined &&
            options.length > CONSTRUCTOR_FACET_CONFIG.searchableFiltersMap[facetName],
        minimize: true,
        activeFacet: options.some((o) => o && o.status === 'selected'),
        promotedFilter: false,
        promotedRank: 0,
        facetValueList: options
            .map((opt) => {
                if (!opt || typeof opt !== 'object') return null;
                const optValue = opt.value || '';
                // preview: strip |dayKey suffix from display value for previewDateFacetOptions facets
                // e.g. "SALE|19Sep2026" → display/URL uses "SALE", Constructor receives full value
                const isHiddenFacet = isPreviewHiddenFacet(
                    facetName,
                    previewDayKey,
                    previewDateFacetOptions
                );
                const displayValue = isHiddenFacet ? stripPreviewSuffix(optValue) : optValue;
                const isSelected = opt.status === 'selected';
                const selectedDisplayValues = selectedValues.map((v) =>
                    isHiddenFacet ? stripPreviewSuffix(v) : v
                );
                let facetValueForUrl;
                if (isSelected) {
                    facetValueForUrl = selectedDisplayValues
                        .filter((v) => v !== displayValue)
                        .sort()
                        .join('|');
                } else {
                    facetValueForUrl = [...selectedDisplayValues, displayValue].sort().join('|');
                }
                const encodedFacetValue = encodeFacetValue(facetValueForUrl);
                const facetsMap = { ...otherFacetsMap };

                let apiUrl;
                if (facetName === 'store_availability') {
                    // store_availability is a toggle — build URL with filters[store_availability] and filters[store_ids]
                    const storeOnlyFacetsMap = { ...otherFacetsMap };
                    delete storeOnlyFacetsMap.store_availability;
                    if (isSelected) {
                        // Toggle OFF: remove store filters, keep everything else
                        apiUrl = buildFacetUrl(
                            pathOnly,
                            storeOnlyFacetsMap,
                            anchorParam,
                            trailingParams
                        );
                    } else {
                        // Toggle ON: put store_availability in facetsMap so it sorts alphabetically before id=
                        const storeOnFacetsMap = {
                            ...storeOnlyFacetsMap,
                            store_availability: 'same+day+pickup',
                        };
                        const storeOnTrailing =
                            trailingParams +
                            (params && params.storeIds ? `&storeIds=${params.storeIds}` : '');
                        apiUrl = buildFacetUrl(
                            pathOnly,
                            storeOnFacetsMap,
                            anchorParam,
                            storeOnTrailing
                        );
                    }
                } else {
                    if (encodedFacetValue) facetsMap[urlKey] = encodedFacetValue;
                    if (storeActive) facetsMap.store_availability = 'same+day+pickup';
                    apiUrl = buildFacetUrl(
                        pathOnly,
                        facetsMap,
                        anchorParam,
                        trailingParams + storeTrailingSuffix
                    );
                }
                const displayName =
                    isHiddenFacet && opt.display_name
                        ? stripPreviewSuffix(opt.display_name)
                        : opt.display_name || displayValue;
                return {
                    name: displayName || '',
                    selected: isSelected,
                    count: opt.count || 0,
                    apiUrl,
                    icon: (opt.data && opt.data.icon) || '',
                    imageUrl: (opt.data && opt.data.icon) || '',
                    value: (() => {
                        // For avgrating facets, extract numeric value from option name (e.g. "5 rating" → 5)
                        const optName = displayName || displayValue || '';
                        const match = optName.match(/^(\d+)/);
                        return match ? parseInt(match[1], 10) : 0;
                    })(),
                    facetNValue: '',
                    facetValue: displayValue,
                    solrFacetName: facetName,
                };
            })
            .filter(Boolean)
            .sort((a, b) => (b.selected ? 1 : 0) - (a.selected ? 1 : 0)),
    };
}

function transformSortOptions(
    constructorSortOptions,
    term,
    facets,
    params,
    pageType,
    categoryId,
    galleryPath,
    brandParam,
    previewDayKey,
    previewDateFacetOptions
) {
    const pathOnly = getBaseApiPathOnly(term, pageType, categoryId, galleryPath, brandParam);
    const anchorParam = getBaseApiAnchorParam(term, pageType, categoryId, brandParam);

    const facetsMap = {};
    if (Array.isArray(facets)) {
        facets.forEach((facet) => {
            if (!facet || !Array.isArray(facet.options) || !facet.name) return;
            if (facet.name === 'store_availability' || facet.name === 'store_ids') return;
            const selectedVals = facet.options
                .filter((opt) => opt && opt.status === 'selected' && opt.value)
                .map((opt) => opt.value);
            if (selectedVals.length > 0) {
                const isPreviewFacet = isPreviewHiddenFacet(
                    facet.name,
                    previewDayKey,
                    previewDateFacetOptions
                );
                const displayVals = isPreviewFacet
                    ? selectedVals.map(stripPreviewSuffix)
                    : selectedVals;
                facetsMap[facet.name] = encodeFacetValue(displayVals.sort().join('|'));
            }
        });
    }
    // Include store_availability in facets map so it sorts alphabetically with other facets
    if (params && params.storeIds) {
        facetsMap.store_availability = 'same+day+pickup';
    }

    let trailingSortParams =
        (params && params.Nf ? `&Nf=${encodeURIComponent(params.Nf).replace(/%20/g, '+')}` : '') +
        (params && params.Dr ? `&Dr=${encodeURIComponent(params.Dr).replace(/%20/g, '+')}` : '');
    if (params && params.storeIds) trailingSortParams += `&storeIds=${params.storeIds}`;
    else if (params && params.store_ids) trailingSortParams += `&storeIds=${params.store_ids}`;
    if (params && params.cm_re)
        trailingSortParams += `&cm_re=${encodeURIComponent(params.cm_re).replace(/%20/g, '+')}`;
    // Add passthrough params to facetsMap so they sort alphabetically
    const facetNames = Array.isArray(facets) ? facets.map((f) => f && f.name).filter(Boolean) : [];
    Object.assign(facetsMap, getPassthroughParamsMap(params, facetNames));

    const basePath = buildFacetUrl(pathOnly, facetsMap, anchorParam, trailingSortParams);

    // Gallery (browse/PLP) pages show "featured" as the default sort label;
    // search pages show "best match" — mirrors the Java backend FS/BM distinction.
    const isGalleryPage = pageType === 'g';
    const defaultSortName = isGalleryPage ? 'featured' : 'best match';

    if (Array.isArray(constructorSortOptions) && constructorSortOptions.length > 0) {
        const hasRealSortOptions = constructorSortOptions.some(
            (opt) => opt && opt.sort_by && opt.sort_by !== 'relevance'
        );
        if (hasRealSortOptions) {
            return constructorSortOptions
                .map((opt, i) => {
                    if (!opt || typeof opt !== 'object') return null;
                    const sortBy = opt.sort_by || '';
                    const key = sortBy === 'relevance' ? '' : sortBy;
                    const apiUrl = key ? `${basePath}&sort=${key}` : basePath;
                    // Normalize the relevance option display name based on page type
                    const name = sortBy === 'relevance' ? defaultSortName : opt.display_name || '';
                    return {
                        apiUrl,
                        name,
                        key,
                        order: i + 1,
                        selected: opt.status === 'selected',
                    };
                })
                .filter(Boolean);
        }
    }
    return [
        { apiUrl: basePath, name: defaultSortName, key: '', order: 1, selected: true },
        {
            apiUrl: `${basePath}&sort=NA`,
            name: 'new arrivals',
            key: 'NA',
            order: 2,
            selected: false,
        },
        {
            apiUrl: `${basePath}&sort=BS`,
            name: 'best sellers',
            key: 'BS',
            order: 3,
            selected: false,
        },
        {
            apiUrl: `${basePath}&sort=RHL`,
            name: 'ratings high - low',
            key: 'RHL',
            order: 4,
            selected: false,
        },
        {
            apiUrl: `${basePath}&sort=LPLH`,
            name: 'price low - high',
            key: 'LPLH',
            order: 5,
            selected: false,
        },
        {
            apiUrl: `${basePath}&sort=LPHL`,
            name: 'price high - low',
            key: 'LPHL',
            order: 6,
            selected: false,
        },
    ];
}

export const transformConstructorSearchResponse = (cioResponse, options = {}) => {
    if (!cioResponse || typeof cioResponse !== 'object') {
        return getEmptyResponse(
            options && options.pageType,
            options && options.categoryId,
            options && options.galleryPath
        );
    }

    try {
        const response = cioResponse.response || {};
        const request = cioResponse.request || {};
        const resultId = cioResponse.result_id || '';
        const results = Array.isArray(response.results) ? response.results : [];
        const facets = Array.isArray(response.facets) ? response.facets : [];
        const sortOptions = Array.isArray(response.sort_options) ? response.sort_options : [];

        const page = (options && options.page) || request.page || 1;
        const perPage = request.num_results_per_page || 48;
        const total = response.total_num_results || 0;
        const maxPages = (options && options.maxPaginationPages) || 25;
        const disableDeepPagination = options && options.disableDeepPagination !== false; // default true
        const totalPages = disableDeepPagination
            ? Math.min(Math.ceil(total / (perPage || 48)), maxPages)
            : Math.ceil(total / (perPage || 48));
        const term = request.term || '';
        const pageType = (options && options.pageType) || 's';
        const categoryId = (options && options.categoryId) || '';
        const galleryPath = (options && options.galleryPath) || '';
        // Extract brand param for brand pages (from params.brand or params.Brand)
        // Also check if galleryPath indicates a brand page
        const isBrandPage =
            (galleryPath && galleryPath.includes('g/brand')) ||
            (typeof window !== 'undefined' &&
                window.location &&
                window.location.pathname &&
                window.location.pathname.includes('/g/brand'));
        let brandParam =
            (options && options.params && (options.params.brand || options.params.Brand)) || '';
        // If no brand in params but this is a brand page, try to get from window.location
        if (!brandParam && isBrandPage && typeof window !== 'undefined' && window.location) {
            try {
                brandParam = new URLSearchParams(window.location.search).get('brand') || '';
            } catch (e) {
                brandParam = '';
            }
        }

        const baseApiPath = getBaseApiPath(term, pageType, categoryId, galleryPath, brandParam);

        const requestSortBy = request.sort_by || '';
        const currentSort = requestSortBy && requestSortBy !== 'relevance' ? requestSortBy : '';

        const selectedFacets = [];
        const pathOnly = getBaseApiPathOnly(term, pageType, categoryId, galleryPath, brandParam);
        const anchorParam = getBaseApiAnchorParam(term, pageType, categoryId, brandParam);
        const chipPreviewDateFacetOptions = (options && options.previewDateFacetOptions) || [];
        const chipPreviewDayKey = options && options.previewDayKey;
        facets.forEach((facet) => {
            if (!facet || !Array.isArray(facet.options)) return;
            // Skip store_availability and store_ids from showing as selected filter chips
            if (facet.name === 'store_availability' || facet.name === 'store_ids') return;
            const selectedOpts = facet.options.filter((o) => o && o.status === 'selected');
            if (selectedOpts.length > 0) {
                selectedFacets.push({
                    facetName: facet.display_name || facet.name || '',
                    facetId: hashCode(facet.name || ''),
                    facetType: 'checkbox',
                    displayFacetName: true,
                    facetInternalName: facet.name || '',
                    facetValueList: selectedOpts.map((opt) => {
                        const remainingValues = selectedOpts
                            .filter((o) => o.value !== opt.value)
                            .map((o) =>
                                isPreviewHiddenFacet(
                                    facet.name,
                                    chipPreviewDayKey,
                                    chipPreviewDateFacetOptions
                                )
                                    ? stripPreviewSuffix(o.value)
                                    : o.value
                            )
                            .filter(Boolean);
                        // Build facets map for chip URL: all selected facets alphabetically before id=
                        const chipFacetsMap = {};
                        facets.forEach((otherFacet) => {
                            if (
                                !otherFacet ||
                                otherFacet.name === facet.name ||
                                !Array.isArray(otherFacet.options)
                            )
                                return;
                            // Skip store-related facets — handled via storeIds/store_availability params
                            if (
                                otherFacet.name === 'store_availability' ||
                                otherFacet.name === 'store_ids'
                            )
                                return;
                            const otherSelected = otherFacet.options
                                .filter((o) => o && o.status === 'selected' && o.value)
                                .map((o) =>
                                    isPreviewHiddenFacet(
                                        otherFacet.name,
                                        chipPreviewDayKey,
                                        chipPreviewDateFacetOptions
                                    )
                                        ? stripPreviewSuffix(o.value)
                                        : o.value
                                );
                            if (otherSelected.length > 0) {
                                chipFacetsMap[otherFacet.name] = encodeFacetValue(
                                    otherSelected.sort().join('|')
                                );
                            }
                        });
                        if (remainingValues.length > 0) {
                            chipFacetsMap[facet.name] = encodeFacetValue(
                                remainingValues.sort().join('|')
                            );
                        }
                        let chipTrailing =
                            (currentSort ? `&sort=${currentSort}` : '') +
                            (options && options.params && options.params.Nf
                                ? `&Nf=${encodeURIComponent(options.params.Nf).replace(
                                      /%20/g,
                                      '+'
                                  )}`
                                : '') +
                            (options && options.params && options.params.Dr
                                ? `&Dr=${encodeURIComponent(options.params.Dr).replace(
                                      /%20/g,
                                      '+'
                                  )}`
                                : '');
                        if (options && options.params && options.params.storeIds) {
                            chipFacetsMap.store_availability = 'same+day+pickup';
                            chipTrailing += `&storeIds=${options.params.storeIds}`;
                        } else if (options && options.params && options.params.store_ids) {
                            chipFacetsMap.store_availability = 'same+day+pickup';
                            chipTrailing += `&store_ids=${options.params.store_ids}`;
                        }
                        if (options && options.params && options.params.cm_re) {
                            chipTrailing += `&cm_re=${encodeURIComponent(
                                options.params.cm_re
                            ).replace(/%20/g, '+')}`;
                        }
                        // Add passthrough params to chipFacetsMap for alphabetical sorting
                        const chipFacetNames = facets.map((f) => f && f.name).filter(Boolean);
                        Object.assign(
                            chipFacetsMap,
                            getPassthroughParamsMap(options && options.params, chipFacetNames)
                        );
                        const chipApiUrl = buildFacetUrl(
                            pathOnly,
                            chipFacetsMap,
                            anchorParam,
                            chipTrailing
                        );
                        return {
                            name: chipPreviewDayKey
                                ? stripPreviewSuffix(
                                      (opt && opt.display_name) || (opt && opt.value) || ''
                                  )
                                : (opt && opt.display_name) || (opt && opt.value) || '',
                            selected: true,
                            count: (opt && opt.count) || 0,
                            apiUrl: chipApiUrl,
                            facetNValue: '',
                            facetValue: chipPreviewDayKey
                                ? stripPreviewSuffix((opt && opt.value) || '')
                                : (opt && opt.value) || '',
                            solrFacetName: facet.name || '',
                        };
                    }),
                });
            }
        });

        // Inject custom price range (Nf) as selected facet chip
        if (
            options &&
            options.params &&
            options.params.Nf &&
            typeof options.params.Nf === 'string'
        ) {
            const nfVal = options.params.Nf.replace(/\+/g, ' ').trim();
            const nfM = nfVal.match(/^PR\s+(\d+\.?\d*)\s+(\d+\.?\d*)$/i);
            if (nfM) {
                const nfFacetsMap = {};
                facets.forEach((f) => {
                    if (
                        !f ||
                        !Array.isArray(f.options) ||
                        f.name === 'store_availability' ||
                        f.name === 'store_ids'
                    )
                        return;
                    const selected = f.options
                        .filter((o) => o && o.status === 'selected' && o.value)
                        .map((o) => o.value);
                    if (selected.length > 0)
                        nfFacetsMap[f.name] = encodeFacetValue(selected.sort().join('|'));
                });
                let nfTrailing =
                    (currentSort ? `&sort=${currentSort}` : '') +
                    (options.params.Dr
                        ? `&Dr=${encodeURIComponent(options.params.Dr).replace(/%20/g, '+')}`
                        : '');
                if (options.params.storeIds)
                    nfTrailing += `&storeIds=${options.params.storeIds}&store_availability=same+day+pickup`;
                if (options.params.cm_re)
                    nfTrailing += `&cm_re=${encodeURIComponent(options.params.cm_re).replace(
                        /%20/g,
                        '+'
                    )}`;
                selectedFacets.push({
                    facetName: 'Price Range',
                    facetId: hashCode('S1_Price_Range'),
                    facetType: 'prange',
                    displayFacetName: true,
                    facetInternalName: 'S1_Price_Range',
                    facetValueList: [
                        {
                            name: `$${nfM[1]} - $${nfM[2]}`,
                            selected: true,
                            count: 0,
                            apiUrl: buildFacetUrl(pathOnly, nfFacetsMap, anchorParam, nfTrailing),
                            facetNValue: '',
                            facetValue: `$${nfM[1]} - $${nfM[2]}`,
                            solrFacetName: 'Nf',
                        },
                    ],
                });
            }
        }

        // Inject custom discount range (Dr) as selected facet chip
        if (
            options &&
            options.params &&
            options.params.Dr &&
            typeof options.params.Dr === 'string'
        ) {
            const drVal = options.params.Dr.replace(/\+/g, ' ').trim();
            const drM = drVal.match(/^DR\s+(\d+\.?\d*)\s+(\d+\.?\d*)$/i);
            if (drM) {
                const drFacetsMap = {};
                facets.forEach((f) => {
                    if (
                        !f ||
                        !Array.isArray(f.options) ||
                        f.name === 'store_availability' ||
                        f.name === 'store_ids'
                    )
                        return;
                    const selected = f.options
                        .filter((o) => o && o.status === 'selected' && o.value)
                        .map((o) => o.value);
                    if (selected.length > 0)
                        drFacetsMap[f.name] = encodeFacetValue(selected.sort().join('|'));
                });
                let drTrailing =
                    (currentSort ? `&sort=${currentSort}` : '') +
                    (options.params.Nf
                        ? `&Nf=${encodeURIComponent(options.params.Nf).replace(/%20/g, '+')}`
                        : '');
                if (options.params.storeIds)
                    drTrailing += `&storeIds=${options.params.storeIds}&store_availability=same+day+pickup`;
                if (options.params.cm_re)
                    drTrailing += `&cm_re=${encodeURIComponent(options.params.cm_re).replace(
                        /%20/g,
                        '+'
                    )}`;
                selectedFacets.push({
                    facetName: 'Discount Range',
                    facetId: hashCode('S1_Discount_Range'),
                    facetType: 'prange',
                    displayFacetName: true,
                    facetInternalName: 'S1_Discount_Range',
                    facetValueList: [
                        {
                            name: `${drM[1]}% - ${drM[2]}%`,
                            selected: true,
                            count: 0,
                            apiUrl: buildFacetUrl(pathOnly, drFacetsMap, anchorParam, drTrailing),
                            facetNValue: '',
                            facetValue: `${drM[1]}% - ${drM[2]}%`,
                            solrFacetName: 'Dr',
                        },
                    ],
                });
            }
        }

        // Transform related_searches from Constructor response to Solr relatedTerms format
        // Input: [{query, image_url}, ...] (e.g., {query: "blue pants", image_url: "DP0707202509211301M"})
        // Output: [{text, apiUrl, productImgId}, ...] (Solr format for display)
        // Controlled via preferences.constructorIO.common.enableRelatedSearchTransform (default: true)
        const enableRelatedSearchTransform =
            options && options.enableRelatedSearchTransform !== false;
        let relatedTerms = [];
        if (enableRelatedSearchTransform) {
            const rawRelatedSearches =
                response && Array.isArray(response.related_searches)
                    ? response.related_searches
                    : [];
            relatedTerms = rawRelatedSearches
                .map((item) => {
                    // Null-safety: skip null/undefined items
                    if (!item || typeof item !== 'object') return null;
                    const text = (item.query && String(item.query)) || '';
                    if (!text) return null;
                    // Build apiUrl in Solr format: /v1/search-service/s?searchTerm=<text>
                    const encodedTerm = encodeURIComponent(text).replace(/%20/g, '+');
                    const apiUrl = `/v1/search-service/s?searchTerm=${encodedTerm}`;
                    const productImgId = (item.image_url && String(item.image_url)) || '';
                    // Determine sourceType based on image ID pattern:
                    // - If image starts with "DP" (uppercase) → standard product image, no sourceType needed
                    // - If image doesn't start with "DP" → marketing/external image, sourceType = 'MKL'
                    let sourceType = null;
                    if (productImgId && !productImgId.startsWith('DP')) {
                        sourceType = 'MKL';
                    }
                    return { text, apiUrl, productImgId, sourceType };
                })
                .filter(Boolean);
        }

        let requestUrl = `/api${baseApiPath}`;
        if (options && options.params) {
            const p = options.params;
            if (p.storeIds) requestUrl += `&storeIds=${encodeURIComponent(p.storeIds)}`;
            if (p.store_availability)
                requestUrl += `&store_availability=${encodeURIComponent(p.store_availability)}`;
            if (p.geoZip) requestUrl += `&geoZip=${encodeURIComponent(p.geoZip)}`;
        }

        return {
            requestDateTime: new Date().toString(),
            channelName: 'CONSTRUCTOR-IO',
            resultId,
            requestUrl,
            contentType: 'XGNS',
            statusCode: response.redirect ? 302 : 200,
            type: 'XGNS',
            numOfMarketingSlots: 0,
            enableRelatedSearch: true,
            retainMarketingZone: false,
            redirectProductPage: response.redirect !== null,
            keywordRedirectUrl: (() => {
                const redirectUrl =
                    (response.redirect && response.redirect.data && response.redirect.data.url) ||
                    '';
                if (!redirectUrl) return '';
                // If Constructor provides a message with the redirect, append addmsg=true
                // so the target page knows to display the redirect message
                const message =
                    response.redirect && response.redirect.data && response.redirect.data.message;
                if (message) {
                    const sep = redirectUrl.includes('?') ? '&' : '?';
                    return `${redirectUrl}${sep}addmsg=true`;
                }
                return redirectUrl;
            })(),
            keywordRedirectMessage: (() => {
                const msg =
                    (response.redirect &&
                        response.redirect.data &&
                        response.redirect.data.message) ||
                    '';
                return msg.replace(/<search term>/gi, term);
            })(),
            pdPageURL: (() => {
                // Gallery/browse pages (/g) — never redirect to PDP on single result
                // User is browsing a category, single result should stay in grid
                const isBrowsePage = options && options.pageType === 'g';
                if (isBrowsePage) return '';

                if (!response.redirect && total === 1 && results.length === 1) {
                    const singleUrl = (results[0] && results[0].data && results[0].data.url) || '';
                    if (singleUrl) {
                        let redirectUrl = singleUrl;
                        // Append storeIds to single-result redirect URL (same-day-pickup)
                        if (options && options.params && options.params.storeIds) {
                            const sep = redirectUrl.includes('?') ? '&' : '?';
                            redirectUrl += `${sep}storeIds=${encodeURIComponent(
                                options.params.storeIds
                            )}`;
                        }
                        // Append geoZip to single-result redirect URL (localization)
                        if (options && options.params && options.params.geoZip) {
                            const sep = redirectUrl.includes('?') ? '&' : '?';
                            redirectUrl += `${sep}geoZip=${encodeURIComponent(
                                options.params.geoZip
                            )}`;
                        }
                        return redirectUrl;
                    }
                }
                return '';
            })(),
            pageCanonicalURL: buildCanonicalURL(
                term,
                facets,
                pageType,
                categoryId,
                galleryPath,
                brandParam
            ),
            displayFreeSameDayPickup: (() => {
                const storeAvailFacet = facets.find((f) => f && f.name === 'store_availability');
                if (!storeAvailFacet || !Array.isArray(storeAvailFacet.options)) return false;
                return storeAvailFacet.options.some(
                    (opt) =>
                        opt &&
                        (opt.value === 'both' ||
                            opt.value === 'same day pickup' ||
                            opt.display_name === 'same day pickup')
                );
            })(),
            visNavExist: false,
            showAutoscopeMsg: false,
            organicZoneInfo: {
                totalNumRecs: total,
                totalPages,
                currentPage: page,
                nextPage: page < totalPages,
                prevPage: page > 1,
                nextPageNum: page < totalPages ? page + 1 : 0,
                prevPageNum: page > 1 ? page - 1 : 0,
                nextPageOffset: page < totalPages ? page * perPage : 0,
                prevPageOffset: page > 1 ? (page - 2) * perPage : 0,
                ppConsumed: 0,
                nextPageMktTiles: 0,
                galleryMLRanking: false,
                inStockZero: false,
                multipleThumbNail: false,
                brandPage: false,
                products: results.map((r) =>
                    transformProduct(r, term, {
                        templateTypesForMiniPdp: options && options.templateTypesForMiniPdp,
                        promotionExcludeLabels: options && options.promotionExcludeLabels,
                        params: options && options.params,
                        pageType,
                        categoryId: options && options.categoryId,
                        groupImageCategories: options && options.groupImageCategories,
                        previewDayKey: options && options.previewDayKey,
                        selectedFacets: (() => {
                            // Detect which PDP-enabled facets are active in URL params
                            // Default list matches Solr's pdpEnabledFacetNames config
                            const pdpEnabledFacets = (options && options.pdpEnabledFacets) || [
                                'color',
                                'size',
                                'childrens_sizes',
                                'bra_size',
                                'mens_sizes',
                                'bed_size',
                                'size_range',
                                'mens_size_range',
                                'womens_size_range',
                            ];
                            // Map Constructor facet names to facetSelected group names (matches Solr pdpEnabledFacetNames)
                            // Configurable via preferences.constructorIO.search.pdpFacetGroupMap
                            const defaultFacetGroupMap = {
                                color: 'color',
                                size: 'size',
                                childrens_sizes: 'size',
                                bra_size: 'size',
                                mens_sizes: 'size',
                                bed_size: 'product',
                                size_range: 'sizerange',
                                mens_size_range: 'sizerange',
                                womens_size_range: 'sizerange',
                            };
                            const facetGroupMap =
                                (options && options.pdpFacetGroupMap) || defaultFacetGroupMap;
                            const active = [];
                            if (options && options.params) {
                                pdpEnabledFacets.forEach((facet) => {
                                    const key = Object.keys(options.params).find(
                                        (k) => k.toLowerCase() === facet.toLowerCase()
                                    );
                                    if (key && options.params[key]) {
                                        const groupName = facetGroupMap[facet] || facet;
                                        if (!active.includes(groupName)) {
                                            active.push(groupName);
                                        }
                                    }
                                });
                            }
                            return active;
                        })(),
                    })
                ),
            },
            facets: {
                orderedSelectedFacetList: selectedFacets,
                facetList: facets
                    // Filter out facets based on page type
                    // hideFacetsOnSearch: array of facet names to hide on search pages (/s)
                    // hideFacetsOnBrowse: array of facet names to hide on browse pages (/g)
                    .filter((f) => {
                        if (!f || f.name === 'store_ids') return false;
                        const hideFacetsOnSearch = (options && options.hideFacetsOnSearch) || [];
                        const hideFacetsOnBrowse = (options && options.hideFacetsOnBrowse) || [];
                        if (pageType === 's' && hideFacetsOnSearch.includes(f.name)) return false;
                        if (pageType === 'g' && hideFacetsOnBrowse.includes(f.name)) return false;
                        if (f.hidden) {
                            const previewDateFacetOptions =
                                (options && options.previewDateFacetOptions) || [];
                            const facetPreviewDayKey = options && options.previewDayKey;
                            if (!facetPreviewDayKey || !previewDateFacetOptions.includes(f.name))
                                return false;
                        }
                        return true;
                    })
                    .map((f) => {
                        const previewDateFacetOptions =
                            (options && options.previewDateFacetOptions) || [];
                        const facetPreviewDayKey = options && options.previewDayKey;
                        if (
                            isPreviewHiddenFacet(
                                f.name,
                                facetPreviewDayKey,
                                previewDateFacetOptions
                            ) &&
                            Array.isArray(f.options)
                        ) {
                            // eslint-disable-next-line no-param-reassign
                            f = {
                                ...f,
                                options: f.options.filter(
                                    (opt) =>
                                        opt &&
                                        typeof opt.value === 'string' &&
                                        opt.value.endsWith(`|${facetPreviewDayKey}`)
                                ),
                            };
                        }
                        // Map store_availability "both" value to "same day pickup" for URLs
                        if (f.name === 'store_availability' && Array.isArray(f.options)) {
                            const mappedFacet = {
                                ...f,
                                options: f.options.map((opt) => {
                                    if (
                                        opt &&
                                        (opt.value === 'both' ||
                                            opt.value === 'same day pickup' ||
                                            opt.display_name === 'same day pickup')
                                    ) {
                                        return {
                                            ...opt,
                                            value: 'same day pickup',
                                            display_name: 'same day pickup',
                                            ...(options?.params?.storeIds
                                                ? { status: 'selected' }
                                                : {}),
                                        };
                                    }
                                    return opt;
                                }),
                            };
                            return transformFacet(
                                mappedFacet,
                                term,
                                currentSort,
                                facets,
                                options && options.params,
                                pageType,
                                categoryId,
                                galleryPath,
                                brandParam,
                                previewDateFacetOptions,
                                facetPreviewDayKey,
                                options && options.appliedFilters,
                                true
                            );
                        }
                        return transformFacet(
                            f,
                            term,
                            currentSort,
                            facets,
                            options && options.params,
                            pageType,
                            categoryId,
                            galleryPath,
                            brandParam,
                            previewDateFacetOptions,
                            facetPreviewDayKey,
                            options && options.appliedFilters,
                            true
                        );
                    })
                    .filter(Boolean)
                    .filter((facet) => {
                        // Skip facets with only 1 option value (matches Solr behavior)
                        // unless a facet value is already selected, or it's store_availability (always needed for toggle)
                        if (facet.facetInternalName === 'store_availability') return true;
                        if (facet.activeFacet) return true;
                        return facet.facetValueCount > 1;
                    })
                    .reduce((acc, facet) => {
                        // Max facet categories limit (default 9, configurable)
                        // store_availability doesn't count toward the limit (it's a special toggle)
                        const maxFacets = (options && options.maxFacetCategories) || 12;
                        if (facet.facetInternalName === 'store_availability') {
                            acc.push(facet);
                        } else if (
                            acc.filter((f) => f.facetInternalName !== 'store_availability').length <
                            maxFacets
                        ) {
                            acc.push(facet);
                        }
                        return acc;
                    }, [])
                    .map((facet) => {
                        // Retain store params in apiUrls (skip store_availability — used for toggle OFF)
                        if (
                            !options?.params?.storeIds ||
                            facet.facetInternalName === 'store_availability'
                        )
                            return facet;
                        const storeSuffix = `&storeIds=${options.params.storeIds}`;
                        if (facet.facetValueList) {
                            // eslint-disable-next-line no-param-reassign
                            facet.facetValueList = facet.facetValueList.map((fv) => {
                                if (!fv.apiUrl) return fv;
                                /* store_availability is in facetsMap (sorts before id=) — do not strip it.
                                 * Only ensure storeIds is present as a trailing param. */
                                let url = fv.apiUrl.replace(/&store_ids=[^&]*/g, '');
                                if (!url.includes('storeIds')) url += storeSuffix;
                                return { ...fv, apiUrl: url };
                            });
                        }
                        return facet;
                    }),
                regionalfacetMap: {},
                contentType: 'GuidedNavigation',
                showClearAll: (() => {
                    let totalSelected = 0;
                    facets.forEach((f) => {
                        if (f && Array.isArray(f.options) && !isStoreFacet(f.name)) {
                            f.options.forEach((o) => {
                                if (o && o.status === 'selected') totalSelected++;
                            });
                        }
                    });
                    // Count Nf/Dr as selected facets for Clear All visibility
                    if (options && options.params && options.params.Nf) totalSelected++;
                    if (options && options.params && options.params.Dr) totalSelected++;
                    return totalSelected >= 2;
                })(),
                clearAll: `${baseApiPath}&productGridView=medium${
                    currentSort ? `&sort=${currentSort}` : ''
                }`,
                noOfSelectedFacets: selectedFacets.length,
                galleryFacetReorderingIndicator: false,
            },
            sortOptions: transformSortOptions(
                sortOptions,
                term,
                facets,
                options && options.params,
                pageType,
                categoryId,
                galleryPath,
                brandParam,
                options && options.previewDayKey,
                options && options.previewDateFacetOptions
            ),
            relatedTerms,
        };
    } catch (error) {
        return getEmptyResponse(pageType, categoryId, galleryPath, brandParam);
    }
};

function getEmptyResponse(pageType, categoryId, galleryPath, brandParam) {
    let basePath;
    let canonicalPath;
    if (pageType === 'g') {
        if (categoryId) {
            basePath = `/v1/search-service/${galleryPath || 'g'}?id=${encodeURIComponent(
                categoryId
            )}`;
            canonicalPath = `/${galleryPath || 'g'}?id=${encodeURIComponent(categoryId)}`;
        } else if (brandParam) {
            basePath = `/v1/search-service/${galleryPath || 'g'}?brand=${encodeURIComponent(
                brandParam
            )}`;
            canonicalPath = `/${galleryPath || 'g'}?brand=${encodeURIComponent(brandParam)}`;
        } else {
            basePath = '/v1/search-service/s?searchTerm=';
            canonicalPath = '/s?searchTerm=';
        }
    } else {
        basePath = '/v1/search-service/s?searchTerm=';
        canonicalPath = '/s?searchTerm=';
    }
    return {
        requestDateTime: new Date().toString(),
        channelName: 'CONSTRUCTOR-IO',
        resultId: '',
        requestUrl: `/api${basePath}`,
        contentType: 'XGNS',
        statusCode: 200,
        type: 'XGNS',
        numOfMarketingSlots: 0,
        enableRelatedSearch: false,
        retainMarketingZone: false,
        redirectProductPage: false,
        pageCanonicalURL: canonicalPath,
        displayFreeSameDayPickup: true,
        visNavExist: false,
        showAutoscopeMsg: false,
        organicZoneInfo: {
            totalNumRecs: 0,
            totalPages: 0,
            currentPage: 1,
            nextPage: false,
            prevPage: false,
            nextPageNum: 0,
            prevPageNum: 0,
            nextPageOffset: 0,
            prevPageOffset: 0,
            ppConsumed: 0,
            nextPageMktTiles: 0,
            galleryMLRanking: false,
            inStockZero: false,
            multipleThumbNail: false,
            brandPage: false,
            products: [],
        },
        facets: {
            orderedSelectedFacetList: [],
            facetList: [],
            regionalfacetMap: {},
            contentType: 'GuidedNavigation',
            showClearAll: false,
            clearAll: `${basePath}&productGridView=medium`,
            noOfSelectedFacets: 0,
            galleryFacetReorderingIndicator: false,
        },
        sortOptions: [
            {
                apiUrl: '',
                name: pageType === 'g' ? 'featured' : 'best match',
                key: '',
                order: 1,
                selected: true,
            },
            { apiUrl: '', name: 'new arrivals', key: 'NA', order: 2, selected: false },
            { apiUrl: '', name: 'best sellers', key: 'BS', order: 3, selected: false },
            { apiUrl: '', name: 'ratings high - low', key: 'RHL', order: 4, selected: false },
            { apiUrl: '', name: 'price low - high', key: 'LPLH', order: 5, selected: false },
            { apiUrl: '', name: 'price high - low', key: 'LPHL', order: 6, selected: false },
        ],
        relatedTerms: [],
    };
}

/**
 * Parses a DP_PREVIEW_DATE cookie string (e.g. "2024.01.15 2:30 PM") into ISO format
 * (e.g. "2024-01-15T14:30:00.000Z"). Valid dates are today through 21 days out (22 days total).
 * Returns current date if the value is missing, unparseable, or outside valid dates.
 */
const PREVIEW_LEAD_DAYS = 21;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CDT_OFFSET_MS = 360 * 60 * 1000; /* UTC-6, matches PreviewDateSelector hardcoded -06:00 */

/* Formats a Date as YYYY.MM.DD using UTC fields for lexicographic comparison */
function utcDayStr(date) {
    return `${date.getUTCFullYear()}.${String(date.getUTCMonth() + 1).padStart(2, '0')}.${String(
        date.getUTCDate()
    ).padStart(2, '0')}`;
}

export function parsePreviewDateToIso(dateStr) {
    const now = new Date();
    if (!dateStr || typeof dateStr !== 'string') return now.toISOString();

    const match = dateStr.match(/^(\d{4})\.(\d{2})\.(\d{2})\s+(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (!match) return now.toISOString();
    const [, year, month, day, rawHours, minutes, meridiem] = match;

    const inputDate = `${year}.${month}.${day}`;
    const todaysDate = utcDayStr(now);
    const maxDateStr = utcDayStr(new Date(now.getTime() + PREVIEW_LEAD_DAYS * MS_PER_DAY));
    if (inputDate < todaysDate || inputDate > maxDateStr) return now.toISOString();

    /* Convert 12-hour CDT time to UTC ISO */
    let hours = parseInt(rawHours, 10);
    const mer = meridiem.toLowerCase();
    if (mer === 'pm' && hours !== 12) hours += 12;
    if (mer === 'am' && hours === 12) hours = 0;

    return new Date(
        Date.UTC(+year, +month - 1, +day, hours, +minutes) + CDT_OFFSET_MS
    ).toISOString();
}

/**
 * Default list of system/non-facet URL params that should NOT be sent as
 * filters to Constructor.io search API.
 *
 * Can be extended/reduced at runtime via preferences config:
 *   preferences.constructorIO.common.ignoreFilterParams = { add: [], remove: [] }
 */
export { CONSTRUCTOR_IGNORE_FILTER_PARAMS };

/**
 * Returns the final Set of param names to ignore when building Constructor filters.
 * Merges the default list with optional preferences overrides:
 *   preferences.constructorIO.common.ignoreFilterParams.add    → extra params to exclude
 *   preferences.constructorIO.common.ignoreFilterParams.remove → params to stop excluding
 *
 * All param names are normalized to lowercase for case-insensitive matching.
 *
 * @param {Object} preferences - The preferences object from context
 * @returns {Set<string>} - Set of param names to ignore (all lowercase)
 */
export function getConstructorIgnoreParams(preferences) {
    // Normalize all default params to lowercase for case-insensitive matching
    const ignoreSet = new Set(
        CONSTRUCTOR_IGNORE_FILTER_PARAMS.map((p) => (p ? p.toLowerCase() : p))
    );

    const config =
        preferences &&
        preferences.constructorIO &&
        preferences.constructorIO.common &&
        preferences.constructorIO.common.ignoreFilterParams;

    if (config) {
        if (Array.isArray(config.add)) {
            config.add.forEach((param) => {
                if (param) ignoreSet.add(param.toLowerCase());
            });
        }
        if (Array.isArray(config.remove)) {
            config.remove.forEach((param) => {
                if (param) ignoreSet.delete(param.toLowerCase());
            });
        }
    }

    return ignoreSet;
}

export default transformConstructorSearchResponse;

/**
 * Translates Solr Nf/Dr URL params to Constructor filter format.
 * Used by GallerySaga and BOPISSaga to apply price/discount range filters.
 *
 * @param {object} params - URL params object (must have Nf and/or Dr keys)
 * @param {object} searchConfig - constructorIO.search preferences
 * @param {object} filters - filters object to mutate (adds filters[field]=value)
 */
export function applyRangeFilters(params, searchConfig, filters) {
    if (!params || !filters) return;
    const config = searchConfig || {};

    // Nf (Price Range): "PR 10 50" → filters[effective_price_min]=10-50
    if (params.Nf && typeof params.Nf === 'string') {
        const nfValue = params.Nf.replace(/\+/g, ' ').trim();
        const nfMatch = nfValue.match(/^PR\s+(\d+\.?\d*)\s+(\d+\.?\d*)$/i);
        if (nfMatch) {
            const priceField = config.priceRangeField || 'effective_price_min';
            filters[`filters[${priceField}]`] = [`${nfMatch[1]}-${nfMatch[2]}`];
        }
    }

    // Dr (Discount Range): "DR 10 50" → filters[min_prc_dscntd_pc]=10-50
    if (params.Dr && typeof params.Dr === 'string') {
        const drValue = params.Dr.replace(/\+/g, ' ').trim();
        const drMatch = drValue.match(/^DR\s+(\d+\.?\d*)\s+(\d+\.?\d*)$/i);
        if (drMatch) {
            const discountField = config.discountRangeField || 'min_prc_dscntd_pc';
            filters[`filters[${discountField}]`] = [`${drMatch[1]}-${drMatch[2]}`];
        }
    }
}
